import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Scissors, Check, X, Loader2, Sparkles, ArrowRight, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TenantSignupPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    business_name: "", slug: "",
    owner_name: "", owner_email: "", owner_phone: "", owner_password: "",
    primary_location_name: "", num_locations: 1,
  });
  const [slugStatus, setSlugStatus] = useState({ checking: false, available: null, reason: "", suggestions: [] });
  const [submitting, setSubmitting] = useState(false);

  // Auto-suggest slug from business name
  useEffect(() => {
    if (form.slug) return;
    const suggested = form.business_name.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 30);
    if (suggested) setForm(f => ({ ...f, slug: suggested }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.business_name]);

  // Debounced slug availability check
  useEffect(() => {
    if (!form.slug) { setSlugStatus({ checking: false, available: null, reason: "", suggestions: [] }); return; }
    setSlugStatus(s => ({ ...s, checking: true }));
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/tenants/slug-check`, { params: { slug: form.slug } });
        setSlugStatus({ checking: false, ...res.data });
      } catch {
        setSlugStatus({ checking: false, available: false, reason: "Couldn't verify", suggestions: [] });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.slug]);

  const canStep1 = form.business_name.trim().length >= 2 && slugStatus.available;
  // Either email OR phone is required (or both). Phone needs at least 10 digits.
  const phoneDigits = (form.owner_phone || "").replace(/\D/g, "");
  const hasEmail = form.owner_email.includes("@");
  const hasPhone = phoneDigits.length >= 10;
  const canStep2 = form.owner_name.trim().length >= 2 && (hasEmail || hasPhone) && form.owner_password.length >= 6;

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/tenants/signup`, form);
      login(res.data.user, res.data.token);
      toast.success(`Welcome to ${form.business_name}!`);
      navigate(`/dashboard`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create workspace");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-violet-950 flex items-center justify-center p-6" data-testid="tenant-signup-page">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 -left-40 w-96 h-96 rounded-full bg-rose-500/10 blur-3xl" />
        <div className="absolute bottom-0 -right-40 w-96 h-96 rounded-full bg-violet-500/10 blur-3xl" />
      </div>

      <div className="relative w-full max-w-xl bg-white/5 backdrop-blur-xl border border-white/10 rounded-[32px] p-8 sm:p-10">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-rose-500 via-violet-500 to-teal-500 flex items-center justify-center">
            <Scissors className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <span className="text-white font-['Outfit'] text-lg font-semibold tracking-tight">SalonFlow OS</span>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-1.5 mb-8" data-testid="signup-progress">
          {[1, 2, 3].map(s => (
            <div key={s} className={`h-1 flex-1 rounded-full transition-all ${s <= step ? "bg-rose-500" : "bg-white/10"}`} />
          ))}
        </div>

        {step === 1 && (
          <div>
            <div className="mb-6">
              <p className="text-[10px] font-bold uppercase tracking-widest text-rose-400 mb-2 font-['Manrope']">Step 1 of 3</p>
              <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-white">Let's name your workspace.</h1>
              <p className="text-white/50 font-['Manrope'] mt-1 text-sm">Pick your salon name and a unique web address.</p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1.5 block font-['Manrope']">Salon name</label>
                <Input data-testid="signup-business-name" value={form.business_name}
                  onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))}
                  placeholder="e.g., Blush Beauty Bar"
                  className="h-12 bg-white/5 border-white/10 text-white rounded-xl placeholder:text-white/20" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1.5 block font-['Manrope']">Workspace URL</label>
                <div className="flex items-center bg-white/5 border border-white/10 rounded-xl overflow-hidden h-12 px-4">
                  <span className="text-white/40 text-sm font-mono">app.com/</span>
                  <input data-testid="signup-slug" value={form.slug}
                    onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))}
                    placeholder="yoursalon"
                    className="bg-transparent flex-1 text-white text-sm font-mono outline-none placeholder:text-white/20" />
                  {form.slug && (
                    <span className="ml-2 flex items-center">
                      {slugStatus.checking ? <Loader2 className="w-4 h-4 text-white/40 animate-spin" /> :
                        slugStatus.available ? <Check className="w-4 h-4 text-emerald-400" data-testid="slug-available-icon" /> :
                        <X className="w-4 h-4 text-rose-400" data-testid="slug-unavailable-icon" />}
                    </span>
                  )}
                </div>
                {form.slug && !slugStatus.checking && !slugStatus.available && slugStatus.reason && (
                  <p className="text-rose-400 text-xs mt-1.5 font-['Manrope']" data-testid="slug-error">{slugStatus.reason}</p>
                )}
                {slugStatus.suggestions?.length > 0 && (
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="slug-suggestions">
                    <span className="text-white/30 text-[10px] font-['Manrope'] uppercase tracking-widest">Try:</span>
                    {slugStatus.suggestions.slice(0, 3).map(s => (
                      <button key={s} onClick={() => setForm(f => ({ ...f, slug: s }))}
                        className="text-[11px] text-rose-300 hover:text-rose-200 font-mono px-2 py-0.5 rounded bg-white/5 hover:bg-white/10">
                        {s}
                      </button>
                    ))}
                  </div>
                )}
                {slugStatus.available && (
                  <p className="text-emerald-400 text-xs mt-1.5 font-['Manrope'] flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />Available — your private workspace URL!
                  </p>
                )}
              </div>
            </div>
            <Button onClick={() => setStep(2)} disabled={!canStep1}
              data-testid="signup-step1-next"
              className="w-full mt-6 h-12 rounded-full bg-white text-slate-900 hover:bg-slate-100 font-semibold disabled:opacity-30">
              Continue <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="mb-6">
              <p className="text-[10px] font-bold uppercase tracking-widest text-rose-400 mb-2 font-['Manrope']">Step 2 of 3</p>
              <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-white">Owner account.</h1>
              <p className="text-white/50 font-['Manrope'] mt-1 text-sm">You'll use these credentials to sign in.</p>
            </div>
            <div className="space-y-3">
              <Input data-testid="signup-owner-name" value={form.owner_name} onChange={e => setForm(f => ({ ...f, owner_name: e.target.value }))}
                placeholder="Your full name" className="h-12 bg-white/5 border-white/10 text-white rounded-xl placeholder:text-white/20" />
              <Input data-testid="signup-owner-email" type="email" value={form.owner_email} onChange={e => setForm(f => ({ ...f, owner_email: e.target.value }))}
                placeholder="Email address (optional if you provide phone)" className="h-12 bg-white/5 border-white/10 text-white rounded-xl placeholder:text-white/20" />
              <Input data-testid="signup-owner-phone" type="tel" inputMode="tel" autoComplete="tel" value={form.owner_phone} onChange={e => setForm(f => ({ ...f, owner_phone: e.target.value }))}
                placeholder="Phone number (optional if you provide email)" className="h-12 bg-white/5 border-white/10 text-white rounded-xl placeholder:text-white/20" />
              <Input data-testid="signup-owner-password" type="password" value={form.owner_password} onChange={e => setForm(f => ({ ...f, owner_password: e.target.value }))}
                placeholder="Password (min 6 characters)" className="h-12 bg-white/5 border-white/10 text-white rounded-xl placeholder:text-white/20" />
              <p className="text-[11px] text-white/40 font-['Manrope']">Provide email, phone, or both — you'll use whichever you give to sign in.</p>
            </div>
            <div className="flex gap-2 mt-6">
              <Button variant="outline" onClick={() => setStep(1)} className="h-12 px-5 rounded-full border-white/20 bg-transparent text-white hover:bg-white/10">Back</Button>
              <Button onClick={() => setStep(3)} disabled={!canStep2}
                data-testid="signup-step2-next"
                className="flex-1 h-12 rounded-full bg-white text-slate-900 hover:bg-slate-100 font-semibold disabled:opacity-30">
                Continue <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <div className="mb-6">
              <p className="text-[10px] font-bold uppercase tracking-widest text-rose-400 mb-2 font-['Manrope']">Step 3 of 3</p>
              <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-white">First location.</h1>
              <p className="text-white/50 font-['Manrope'] mt-1 text-sm">You can add more later.</p>
            </div>
            <div className="space-y-3">
              <Input data-testid="signup-location-name" value={form.primary_location_name} onChange={e => setForm(f => ({ ...f, primary_location_name: e.target.value }))}
                placeholder={`e.g., ${form.business_name || "Downtown"}`}
                className="h-12 bg-white/5 border-white/10 text-white rounded-xl placeholder:text-white/20" />
              <div className="bg-white/5 rounded-xl p-4 flex items-start gap-3 border border-white/10">
                <Building2 className="w-5 h-5 text-violet-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-white font-['Outfit']">14-day free trial</p>
                  <p className="text-xs text-white/50 font-['Manrope'] mt-0.5">Full access to every feature. No credit card needed.</p>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button variant="outline" onClick={() => setStep(2)} className="h-12 px-5 rounded-full border-white/20 bg-transparent text-white hover:bg-white/10">Back</Button>
              <Button onClick={submit} disabled={submitting}
                data-testid="signup-submit"
                className="flex-1 h-12 rounded-full bg-gradient-to-r from-rose-500 to-violet-500 text-white font-semibold hover:from-rose-600 hover:to-violet-600 disabled:opacity-50">
                {submitting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Creating…</> : <>Create Workspace <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>
            </div>
          </div>
        )}

        <p className="text-center text-white/30 text-xs mt-8 font-['Manrope']">
          Already have a workspace? <Link to="/login" className="text-white/60 hover:text-white">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
